import { useState, useEffect } from "react";
import "./App.css";

const API = process.env.REACT_APP_API_URL || "/api";

const EXCERPT_LENGTH = 120;

function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T12:00:00");
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function getExcerpt(body, maxLen = EXCERPT_LENGTH) {
  if (!body || typeof body !== "string") return "";
  const stripped = body.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  if (stripped.length <= maxLen) return stripped;
  const slice = stripped.slice(0, maxLen);
  const lastSpace = slice.lastIndexOf(" ");
  const end = lastSpace > maxLen * 0.6 ? lastSpace : maxLen;
  return slice.slice(0, end).trim() + "…";
}

function App() {
  const [posts, setPosts] = useState([]);
  const [links, setLinks] = useState([]);
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      try {
        const [postsRes, linksRes, authorRes] = await Promise.all([
          fetch(`${API}/posts/`),
          fetch(`${API}/links/`),
          fetch(`${API}/author/`).catch(() => null),
        ]);
        if (!postsRes.ok || !linksRes.ok) throw new Error("Failed to load");
        const postsData = await postsRes.json();
        const linksData = await linksRes.json();
        const authorData = authorRes?.ok ? await authorRes.json() : null;
        if (!cancelled) {
          setPosts(postsData);
          setLinks(linksData);
          setAuthor(authorData);
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchData();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="app"><p className="loading">Loading…</p></div>;
  if (error) return <div className="app"><p className="error">Error: {error}. Is the backend running on port 8000?</p></div>;

  if (selectedPost) {
    return (
      <div className="app">
        <div className="app-bar" aria-hidden>
          <div className="app-bar-segment" />
          <div className="app-bar-segment" />
          <div className="app-bar-segment" />
          <div className="app-bar-segment" />
        </div>
        <div className="app-content">
          <a href="#stories" className="back-link" onClick={(e) => { e.preventDefault(); setSelectedPost(null); }}>
            ← All stories
          </a>
          <article className="story story--detail" style={{ margin: "0 1.5rem" }}>
            <h1 className="story-title">{selectedPost.title}</h1>
            {selectedPost.date && (
              <p className="story-date">{formatDate(selectedPost.date)}</p>
            )}
            {selectedPost.subtitle && <p className="story-subtitle">{selectedPost.subtitle}</p>}
            <div className="story-body" dangerouslySetInnerHTML={{ __html: selectedPost.body }} />
          </article>
        </div>
        <footer className="app-footer">
          <span>© 2026</span>
          <a href="https://blankpagedesign.org" target="_blank"><span>Built by BPD</span></a>
        </footer>
      </div>
    );
  }

  const stories = [
    ...posts.map((p) => ({ ...p, type: "post" })),
    ...links.map((l) => ({ ...l, type: "link" })),
  ].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

  return (
    <div className="app">
      <div className="app-bar" aria-hidden>
        <div className="app-bar-segment" />
        <div className="app-bar-segment" />
        <div className="app-bar-segment" />
        <div className="app-bar-segment" />
      </div>
      <div className="app-content">
        <header>
          <h1 className="site-title">Faith Hug Writes</h1>
        </header>
        <ul className="stories" id="stories">
          {stories.map((item) =>
            item.type === "post" ? (
              <li key={`post-${item.id}`} className="story story--post">
                <button
                  type="button"
                  className="story-link"
                  onClick={() => setSelectedPost(item)}
                >
                  <h2 className="story-title">{item.title}</h2>
                  {item.date && (
                    <p className="story-date">{formatDate(item.date)}</p>
                  )}
                  {item.subtitle && <p className="story-subtitle">{item.subtitle}</p>}
                  {item.body && (
                    <p className="story-excerpt">{getExcerpt(item.body)}</p>
                  )}
                  {(item.tag || "Essay") && (
                    <span className="story-meta story-meta--essay">
                      {item.tag || "Essay"}
                    </span>
                  )}
                </button>
              </li>
            ) : (
              <li key={`link-${item.id}`} className="story story--link">
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="story-link"
                >
                  <h2 className="story-title">
                    {item.title}
                    <span className="external-icon" aria-hidden>↗</span>
                  </h2>
                  {item.description && <p className="story-subtitle">{item.description}</p>}
                  <span className="story-meta story-meta--elsewhere">Elsewhere</span>
                </a>
              </li>
            )
          )}
        </ul>
        {stories.length === 0 && <p className="loading">No stories yet.</p>}
        {author && (author.name || author.bio) && (
          <section className="about" aria-label="About the author">
            <div className="about-head" aria-hidden />
            <div className="about-photo">
              <img
                src={`${process.env.PUBLIC_URL || ""}/assets/Faith-3-1-scaled.jpg`}
                alt=""
                className="about-photo-img"
              />
            </div>
            <div className="about-info">
              <p className="about-label">About</p>
              {author.bio && (
                <p
                  className="about-bio"
                  dangerouslySetInnerHTML={{ __html: author.bio }}
                />
              )}
            </div>
          </section>
        )}
      </div>
      <footer className="app-footer">
        <span>© Copyright 2026</span>
        <a href="https://blankpagedesign.org" target="_blank"><span>Built by BPD</span></a>
      </footer>
    </div>
  );
}

export default App;
